import type { BlockByBlockGame, Piece, Cell } from "./types.js";
import { COLS, ROWS, PIECES } from "./types.js";

function randomPiece(): Piece {
  const p = PIECES[Math.floor(Math.random() * PIECES.length)];
  return {
    shape: p.shape.map((row) => [...row]),
    color: p.color,
    x: Math.floor((COLS - p.shape[0].length) / 2),
    y: 0,
  };
}

export function createGame(): BlockByBlockGame {
  const board: Cell[][] = Array.from({ length: ROWS }, () => Array(COLS).fill(0 as Cell));
  return {
    board,
    current: randomPiece(),
    next: randomPiece(),
    score: 0,
    level: 1,
    lines: 0,
    gameOver: false,
    dropTimer: 0,
    dropInterval: 800,
  };
}

function collides(board: Cell[][], shape: number[][], x: number, y: number): boolean {
  for (let r = 0; r < shape.length; r++) {
    for (let c = 0; c < shape[r].length; c++) {
      if (!shape[r][c]) continue;
      const bx = x + c;
      const by = y + r;
      if (bx < 0 || bx >= COLS || by >= ROWS) return true;
      if (by < 0) continue;
      if (board[by][bx] !== 0) return true;
    }
  }
  return false;
}

function rotateCW(shape: number[][]): number[][] {
  const rows = shape.length;
  const cols = shape[0].length;
  const result: number[][] = Array.from({ length: cols }, () => Array(rows).fill(0));
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      result[c][rows - 1 - r] = shape[r][c];
    }
  }
  return result;
}

function lockPiece(board: Cell[][], piece: Piece): Cell[][] {
  const b = board.map((row) => [...row]);
  for (let r = 0; r < piece.shape.length; r++) {
    for (let c = 0; c < piece.shape[r].length; c++) {
      if (!piece.shape[r][c]) continue;
      const by = piece.y + r;
      const bx = piece.x + c;
      if (by >= 0 && by < ROWS && bx >= 0 && bx < COLS) {
        b[by][bx] = piece.color;
      }
    }
  }
  return b;
}

function clearLines(board: Cell[][]): { board: Cell[][]; cleared: number } {
  const kept = board.filter((row) => row.some((cell) => cell === 0));
  const cleared = ROWS - kept.length;
  const empty: Cell[][] = Array.from({ length: cleared }, () => Array(COLS).fill(0 as Cell));
  return { board: [...empty, ...kept], cleared };
}

export function tick(game: BlockByBlockGame, dt: number, actions: { left: boolean; right: boolean; down: boolean; rotate: boolean; drop: boolean }): BlockByBlockGame {
  let g = { ...game, dropTimer: game.dropTimer + dt };
  if (g.gameOver) return g;
  let piece = g.current;
  if (!piece) return g;

  if (actions.rotate) {
    const rotated = rotateCW(piece.shape);
    if (!collides(g.board, rotated, piece.x, piece.y)) {
      piece = { ...piece, shape: rotated };
    } else {
      const kicks = [1, -1, 2, -2];
      for (const kick of kicks) {
        if (!collides(g.board, rotated, piece.x + kick, piece.y)) {
          piece = { ...piece, shape: rotated, x: piece.x + kick };
          break;
        }
      }
    }
  }

  if (actions.left) {
    if (!collides(g.board, piece.shape, piece.x - 1, piece.y)) {
      piece = { ...piece, x: piece.x - 1 };
    }
  }
  if (actions.right) {
    if (!collides(g.board, piece.shape, piece.x + 1, piece.y)) {
      piece = { ...piece, x: piece.x + 1 };
    }
  }

  if (actions.drop) {
    while (!collides(g.board, piece.shape, piece.x, piece.y + 1)) {
      piece = { ...piece, y: piece.y + 1 };
    }
    g.dropTimer = g.dropInterval;
  }

  if (actions.down || g.dropTimer >= g.dropInterval) {
    if (!collides(g.board, piece.shape, piece.x, piece.y + 1)) {
      piece = { ...piece, y: piece.y + 1 };
      g.dropTimer = 0;
      if (actions.down) g.score += 1;
    } else {
      g.board = lockPiece(g.board, piece);
      const { board: clearedBoard, cleared } = clearLines(g.board);
      g.board = clearedBoard;
      g.lines += cleared;
      if (cleared === 1) g.score += 100 * g.level;
      else if (cleared === 2) g.score += 300 * g.level;
      else if (cleared === 3) g.score += 500 * g.level;
      else if (cleared >= 4) g.score += 800 * g.level;

      const newLevel = Math.floor(g.lines / 10) + 1;
      if (newLevel > g.level) {
        g.level = newLevel;
        g.dropInterval = Math.max(100, 800 - (newLevel - 1) * 60);
      }

      g.current = g.next!;
      g.next = randomPiece();
      if (collides(g.board, g.current.shape, g.current.x, g.current.y)) {
        g.gameOver = true;
      }
      g.dropTimer = 0;
      return g;
    }
  }

  g.current = piece;
  return g;
}

export function getGhostY(board: Cell[][], piece: Piece): number {
  let y = piece.y;
  while (!collides(board, piece.shape, piece.x, y + 1)) y++;
  return y;
}
